import { Label } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import {
  formatInventoryAmlMpnLabel,
  type InventoryAmlMpnAlternate,
} from "@/lib/inventoryAmlMpn";

export function InventoryAmlMpnPicker(props: {
  alternates: InventoryAmlMpnAlternate[];
  selectedApprovedComponentId: string | null;
  /** When set and not on `alternates`, shows a warning before the picker. */
  savedApprovedComponentId?: string | null;
  disabled?: boolean;
  /** When true, a single manufacturer part number uses the same field height as a text input. */
  field?: boolean;
  onSelect: (approvedComponentId: string) => void;
}): JSX.Element | null {
  if (props.alternates.length === 0) return null;

  const savedNotOnAml =
    props.savedApprovedComponentId != null
    && props.savedApprovedComponentId.trim() !== ""
    && !props.alternates.some((row) => row.id === props.savedApprovedComponentId);

  if (props.alternates.length === 1) {
    const alternate = props.alternates[0]!;
    return (
      <div className="shrink-0 space-y-2">
        {savedNotOnAml ? (
          <p className="text-sm text-amber-900">
            This carton&apos;s saved MPN is not on the current approved vendor list. Saving will use the
            approved alternate below.
          </p>
        ) : null}
        {props.field === true ? (
          <div>
            <Label>Manufacturer part number</Label>
            <div className="mt-1 flex h-10 items-center rounded-md border border-slate-300 bg-slate-50 px-3 font-mono text-sm text-slate-900">
              {formatInventoryAmlMpnLabel(alternate)}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">
              Manufacturer part number
            </p>
            <p className="mt-1 font-mono text-sm text-slate-900">{formatInventoryAmlMpnLabel(alternate)}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="shrink-0 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
      {savedNotOnAml ? (
        <p className="mb-2 text-sm text-amber-950">
          This carton&apos;s saved MPN is not on the approved vendor list. Pick an approved alternate
          before saving.
        </p>
      ) : null}
      <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-900">
        Manufacturer part number
      </p>
      <p className="mt-0.5 text-[11px] text-amber-900">
        This CPN has multiple approved MPNs. Pick the label on this carton, or change it if the label
        was wrong.
      </p>
      <div className="mt-2 flex flex-col gap-1.5">
        {props.alternates.map((alternate) => {
          const selected = props.selectedApprovedComponentId === alternate.id;
          return (
            <button
              key={alternate.id}
              type="button"
              disabled={props.disabled}
              onClick={() => props.onSelect(alternate.id)}
              className={cn(
                "rounded-md border px-2.5 py-2 text-left text-sm transition",
                selected
                  ? "border-brand-500 bg-brand-50 font-semibold text-brand-900"
                  : "border-amber-200 bg-white text-slate-800 hover:border-amber-300",
                props.disabled && "cursor-not-allowed opacity-60",
              )}
            >
              <span className="font-mono text-xs">{formatInventoryAmlMpnLabel(alternate)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
